import { FC, useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import { apiUseCampaignSetupFromTemplate, CampaignSetupFromTemplateStatus } from 'src/api/campaigns';
import { UiToast } from 'src/components/UI/toasts';
import { ConfirmContext } from 'src/containers/Common';
import { ROUTE_CUSTOMER_TYPE } from 'src/routes';
import styles from 'src/styles/common/templateGallery.module.scss';
import { TemplateGalleryActivities } from 'src/components/Modals/TemplateGallery/screens/Details/Activities';
import { customHistory } from 'src/utils/customHistory';
import { ApiError, useRequest } from 'src/utils/hooks/useRequest';
import { useNsTHelper } from 'src/utils/i18n';
import { IconChevronRight } from 'src/components/Icons/ChevronRight';
import { TemplateGalleryTemplate } from 'src/components/Modals/TemplateGallery/components/Template';
import { useAnyRequestState } from 'src/utils/hooks/useAnyRequestState';
import { TemplateGalleryDetailsResponseType } from 'src/models/Modals/templateGallery';
import { apiLoadCampaignTemplateDetails } from 'src/api/organizerTemplate';
import { UiLoader } from 'src/components/UI/loaders';
import { UiButton } from 'src/components/UI/Button';
import { hardRedirect } from 'src/utils/hardRedirect';
import { createLinkToCampaignSetupByPermanentUrl } from 'src/utils/url';
import { SetScreenType } from 'src/components/Modals/TemplateGallery/index';
import { TemplateGalleryDetailsScreenPricingModal } from 'src/components/Modals/TemplateGallery/screens/Details/PricingModal';
import { deCommonCurrentPartnerSelector } from 'src/store/commonPart/selector';

type Props = {
  id: string | undefined;
  setScreen: SetScreenType;
  onHide: (value: boolean) => void;
};

export const TemplateGalleryDetailsScreen: FC<Props> = ({ setScreen, id, onHide }) => {
  const tHelper = useNsTHelper('modals.templateGallery', 'details');

  const { CampaignTerminologySingular } = useSelector(deCommonCurrentPartnerSelector);

  const [model] = useAnyRequestState<TemplateGalleryDetailsResponseType>(null, {
    apiFunction: apiLoadCampaignTemplateDetails,
    data: id,
    onSuccess: ({ IsNotFound }) => {
      if (IsNotFound) {
        setScreen('all');
      }
    },
  });

  const [isLoading, setIsLoading] = useState(false);

  const { apiCaller } = useRequest();

  const confirm = useContext(ConfirmContext);

  const handleClick = useCallback(() => {
    if (model?.ID) {
      apiCaller(apiUseCampaignSetupFromTemplate, {
        ID: model.ID,
        OverridenOrganizationID: window.managedCampaignSettings?.organizationId,
        OverridenUserID: window.managedCampaignSettings?.userId,
        IsCreatedByAdmin: window.managedCampaignSettings?.isCreatedByAdmin || false,
      })
        .then((response) => {
          if (response.Status === CampaignSetupFromTemplateStatus.Success) {
            let url = response.PermanentUrl;

            if (response.ShowOfflineRaffleModal) {
              url = `${response.PermanentUrl}?showOfflineRaffleModal=true`;
            } else if (response.ShowNonUsRaffleSweepstakesModal) {
              url = `${response.PermanentUrl}?showNonUsRaffleSweepstakesModal=true`;
            }

            hardRedirect(createLinkToCampaignSetupByPermanentUrl(url));
          } else if (response.Status === CampaignSetupFromTemplateStatus.NeedMoreInformation) {
            confirm({
              header: tHelper('needMoreInfoConfirm.header'),
              body: tHelper('needMoreInfoConfirm.body', { campaignSingularTerminology: CampaignTerminologySingular }),
              singleButton: true,
              successBtnText: tHelper('needMoreInfoConfirm.successBtnText'),
              successBtnOnClick: () => {
                customHistory.push(ROUTE_CUSTOMER_TYPE);
              },
            });
          }
        })
        .catch((response: ApiError) => {
          UiToast.error(response.errorMessages);
        })
        .finally(() => {
          setIsLoading(false);
        });

      setIsLoading(true);
    }
  }, [CampaignTerminologySingular, apiCaller, confirm, model?.ID, tHelper]);

  const [isShowModal, setIsShowModal] = useState(false);
  const showModal = useCallback(() => {
    setIsShowModal(true);
    onHide(true);
  }, [onHide]);

  const handleCloseModal = () => {
    setIsShowModal(false);
    onHide(false);
  };

  return (
    <div className={styles.content}>
      {model && !model.IsNotFound ? (
        <>
          <div className={styles.info}>
            <div className={styles.mainInfo}>
              <TemplateGalleryTemplate
                Name={model.Name}
                CategoryNames={model.CategoryNames}
                MediaUri={model.MediaUri}
              />
            </div>
          </div>
          <div className={styles.about}>
            {model.Description && (
              <>
                <div className={styles.subhead}>{tHelper('about')}</div>
                {/* eslint-disable-next-line react/no-danger */}
                <div dangerouslySetInnerHTML={{ __html: model.Description }} />
              </>
            )}
          </div>
          <TemplateGalleryActivities items={model.TemplateFeatures} />
          <div className={styles.footer}>
            <UiButton
              className={styles.button}
              loading={isLoading}
              disabled={model.IsTemplateUnavailable}
              tooltipText={model.IsTemplateUnavailable ? model.ReasonOfTemplateIsUnavailable : undefined}
              onClick={model.HasRestrictedFeatures ? showModal : handleClick}
              primaryNew
            >
              <span className={styles.separator} />
              <span>{tHelper('useThisTemplate')}</span>
              <IconChevronRight className={styles.icon} />
            </UiButton>
          </div>

          {isShowModal && (
            <TemplateGalleryDetailsScreenPricingModal
              onClose={handleCloseModal}
              onSuccess={handleClick}
              successLoading={isLoading}
              percent={model.ServiceFeePercent}
              campaignTerminologySingular={CampaignTerminologySingular}
            />
          )}
        </>
      ) : (
        <UiLoader centered className={styles.loader} />
      )}
    </div>
  );
};
